import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "switch-account-dropdown" }
const _hoisted_2 = { class: "switch-account-dropdown__sticky-header" }
const _hoisted_3 = { class: "switch-account-dropdown__label" }

import { computed, inject, onMounted, ref } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import Heading from 'vcomponents/common/Heading/Heading.vue';

interface Account {
    uuid: string;
    externalUserId: number;
    externalUserType: string;
    customer?: { name?: string };
    externalAttributes?: { third_name?: string };
    switchUrl: string;
    displayName: string;
}

interface Props {
    accounts: Account[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SwitchAccountDropdown',
  props: {
    accounts: {}
  },
  emits: ["select"],
  setup(__props: any) {

const props = __props;


const $t = inject('$t') as Translator;

const translations: Record<string, string> = {
    title: $t.__('common.labels.switch_account'),
    placeholder: $t.__('common.labels.search'),
    emptyTitle: $t.__('header.Nav.no_account_found'),
    emptyCaption: $t.__('header.Nav.no_account_found_caption'),
};

const searchQuery = ref('');
const searchInput = ref<{ setFocus: () => void } | null>(null);

const normalizeForSearch = (value: string): string => {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[._\-/\\]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

const buildSearchMap = (value: string): { normalized: string; originalIndices: number[] } => {
    let normalized = '';
    const originalIndices: number[] = [];
    let lastIsSpace = true;
    for (let i = 0; i < value.length; i++) {
        const decomposed = value[i].normalize('NFD');
        for (const sub of decomposed) {
            if (/[\u0300-\u036f]/.test(sub)) {
                continue;
            }
            if (/[._\-/\\]/.test(sub)) {
                continue;
            }
            const lower = sub.toLowerCase();
            if (/\s/.test(lower)) {
                if (lastIsSpace) {
                    continue;
                }
                normalized += ' ';
                originalIndices.push(i);
                lastIsSpace = true;
            } else {
                normalized += lower;
                originalIndices.push(i);
                lastIsSpace = false;
            }
        }
    }
    while (normalized.endsWith(' ')) {
        normalized = normalized.slice(0, -1);
        originalIndices.pop();
    }
    return { normalized, originalIndices };
};

const matchesQuery = (displayName: string, query: string): boolean => {
    const haystack = normalizeForSearch(displayName);
    const tokens = normalizeForSearch(query).split(' ').filter(Boolean);
    if (tokens.length === 0) {
        return true;
    }
    return tokens.every((token) => haystack.includes(token));
};

const getDisplaySegments = (text: string, query: string): { text: string; isMatch: boolean }[] => {
    if (!query) {
        return [{ text, isMatch: false }];
    }
    const { normalized, originalIndices } = buildSearchMap(text);
    const tokens = normalizeForSearch(query).split(' ').filter(Boolean);
    if (tokens.length === 0) {
        return [{ text, isMatch: false }];
    }
    const ranges: { start: number; end: number }[] = [];
    for (const token of tokens) {
        let pos = 0;
        while ((pos = normalized.indexOf(token, pos)) !== -1) {
            ranges.push({
                start: originalIndices[pos],
                end: originalIndices[pos + token.length - 1] + 1,
            });
            pos += token.length;
        }
    }
    if (ranges.length === 0) {
        return [{ text, isMatch: false }];
    }
    ranges.sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [];
    for (const range of ranges) {
        const last = merged[merged.length - 1];
        if (last && last.end >= range.start) {
            last.end = Math.max(last.end, range.end);
        } else {
            merged.push({ ...range });
        }
    }
    const segments: { text: string; isMatch: boolean }[] = [];
    let cursor = 0;
    for (const range of merged) {
        if (cursor < range.start) {
            segments.push({ text: text.slice(cursor, range.start), isMatch: false });
        }
        segments.push({ text: text.slice(range.start, range.end), isMatch: true });
        cursor = range.end;
    }
    if (cursor < text.length) {
        segments.push({ text: text.slice(cursor), isMatch: false });
    }
    return segments;
};

const filteredAccounts = computed<Account[]>(() => {
    if (!searchQuery.value) {
        return props.accounts;
    }
    return props.accounts.filter((account) => matchesQuery(account.displayName, searchQuery.value));
});

const resultsLabel = computed<string>(() =>
    $t.n__('header.Nav.all_accounts_count', null, filteredAccounts.value.length, {
        count: filteredAccounts.value.length,
    }),
);

onMounted(() => {
    setTimeout(() => {
        searchInput.value?.setFocus();
    }, 0);
});

return (_ctx: any,_cache: any) => {
  const _component_CompassInput = _resolveComponent("CompassInput")!
  const _component_CompassMenuSubtitle = _resolveComponent("CompassMenuSubtitle")!
  const _component_CompassMenuItem = _resolveComponent("CompassMenuItem")!
  const _component_CompassEmptyState = _resolveComponent("CompassEmptyState")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(Heading, {
        variant: "h3",
        labelTitle: translations.title,
        prefixColoredIcon: { icon: 'profile', color: 'brand' }
      }, null, 8 /* PROPS */, ["labelTitle"]),
      _createVNode(_component_CompassInput, {
        ref_key: "searchInput",
        ref: searchInput,
        modelValue: searchQuery.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
        placeholder: translations.placeholder,
        prefixIcon: "search",
        type: _ctx.CompassEnums.InputType.SEARCH,
        class: "switch-account-dropdown__search"
      }, null, 8 /* PROPS */, ["modelValue", "placeholder", "type"])
    ]),
    _createVNode(_component_CompassMenuSubtitle, { label: resultsLabel.value }, null, 8 /* PROPS */, ["label"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredAccounts.value, (account) => {
      return (_openBlock(), _createBlock(_component_CompassMenuItem, {
        key: account.uuid,
        onClick: ($event: any) => (_ctx.$emit('select', account))
      }, {
        default: _withCtx(() => [
          _createElementVNode("span", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getDisplaySegments(account.displayName, searchQuery.value), (segment, index) => {
              return (_openBlock(), _createElementBlock("span", {
                key: index,
                class: _normalizeClass({ 'switch-account-dropdown__highlight': segment.isMatch })
              }, _toDisplayString(segment.text), 3 /* TEXT, CLASS */))
            }), 128 /* KEYED_FRAGMENT */))
          ])
        ]),
        _: 2 /* DYNAMIC */
      }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick"]))
    }), 128 /* KEYED_FRAGMENT */)),
    (filteredAccounts.value.length === 0)
      ? (_openBlock(), _createBlock(_component_CompassEmptyState, {
          key: 0,
          illustrationPath: "/ressources/logged/img/illustrations/search-no-results.svg",
          title: translations.emptyTitle,
          caption: translations.emptyCaption,
          class: "switch-account-dropdown__empty-state"
        }, null, 8 /* PROPS */, ["title", "caption"]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})